/**
 * Dialog for inviting a new user to the account
 * Creates an invite link that can be shared via Slack/Teams
 */
import { Check, ChevronDown, Copy, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

interface InviteUserDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

/**
 * Dialog for creating user invites with role and team assignments
 * @param root0 - Component props
 * @param root0.open - Whether the dialog is open
 * @param root0.onOpenChange - Callback when dialog open state changes
 * @param root0.onSuccess - Callback when invite is successfully created
 */
export function InviteUserDialog({
	open,
	onOpenChange,
	onSuccess,
}: InviteUserDialogProps) {
	const [email, setEmail] = useState("");
	const [selectedAccountRoleId, setSelectedAccountRoleId] = useState<string | null>(null);
	const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
	const [selectedTeamRoleId, setSelectedTeamRoleId] = useState<string | null>(null);
	const [inviteUrl, setInviteUrl] = useState<string | null>(null);
	const [accountRoleSelectorOpen, setAccountRoleSelectorOpen] = useState(false);
	const [teamSelectorOpen, setTeamSelectorOpen] = useState(false);
	const [teamRoleSelectorOpen, setTeamRoleSelectorOpen] = useState(false);
	const [confirmImmediately, setConfirmImmediately] = useState(false);

	// Check if current user is super_admin
	const currentUser = useAuthStore((state) => state.user);
	const isSuperAdmin = currentUser?.role === "super_admin";

	// Fetch roles for the account
	const rolesQuery = trpc.roles.list.useQuery();

	// Fetch teams for the account
	const teamsQuery = trpc.permissionGroups.listAccountTeams.useQuery();

	// Create invite mutation
	const createInviteMutation = trpc.permissionGroups.createInvite.useMutation({
		onSuccess: (data) => {
			if (data.confirmedImmediately) {
				// User was confirmed immediately - use special marker
				setInviteUrl("__CONFIRMED__");
			}
			else {
				setInviteUrl(data.inviteUrl);
			}
			onSuccess();
		},
	});

	/**
	 * Handles dialog open/close and resets form on close
	 * @param newOpen - The new open state
	 */
	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			// Reset form state when closing
			setEmail("");
			setSelectedAccountRoleId(null);
			setSelectedTeamIds([]);
			setSelectedTeamRoleId(null);
			setInviteUrl(null);
			setConfirmImmediately(false);
		}
		onOpenChange(newOpen);
	};

	/**
	 * Creates a new invitation
	 */
	const handleCreate = () => {
		if (!selectedAccountRoleId) return;

		createInviteMutation.mutate({
			email,
			accountRoleId: selectedAccountRoleId,
			teamIds: selectedTeamIds.length > 0 ? selectedTeamIds : undefined,
			teamRoleId: selectedTeamIds.length > 0 ? selectedTeamRoleId ?? undefined : undefined,
			confirmImmediately,
		});
	};

	/**
	 * Copies the invite URL to clipboard
	 */
	const handleCopy = async () => {
		if (inviteUrl && inviteUrl !== "__CONFIRMED__") {
			await navigator.clipboard.writeText(inviteUrl);
		}
	};

	/**
	 * Toggles a team selection
	 * @param teamId - The ID of the team to toggle
	 */
	const handleToggleTeam = (teamId: string) => {
		setSelectedTeamIds((prev) =>
			prev.includes(teamId)
				? prev.filter((id) => id !== teamId)
				: [...prev, teamId]
		);
	};

	// Auto-select default viewer role if available
	useEffect(() => {
		if (open && !selectedAccountRoleId && rolesQuery.data) {
			const viewerRole = rolesQuery.data.find((r) => r.slug === "viewer");
			if (viewerRole) {
				setSelectedAccountRoleId(viewerRole.id);
			}
		}
	}, [open, selectedAccountRoleId, rolesQuery.data]);

	// Auto-select team role when teams are selected and no role chosen
	useEffect(() => {
		if (selectedTeamIds.length > 0 && !selectedTeamRoleId && rolesQuery.data) {
			const viewerRole = rolesQuery.data.find((r) => r.slug === "viewer");
			if (viewerRole) {
				setSelectedTeamRoleId(viewerRole.id);
			}
		}
		// Clear team role if no teams selected
		if (selectedTeamIds.length === 0) {
			setSelectedTeamRoleId(null);
		}
	}, [selectedTeamIds, selectedTeamRoleId, rolesQuery.data]);

	const selectedAccountRole = rolesQuery.data?.find((r) => r.id === selectedAccountRoleId);
	const selectedTeamRole = rolesQuery.data?.find((r) => r.id === selectedTeamRoleId);
	const selectedTeams = teamsQuery.data?.filter((t) => selectedTeamIds.includes(t.id)) ?? [];

	const canCreate = email &&
		selectedAccountRoleId &&
		(selectedTeamIds.length === 0 || selectedTeamRoleId) &&
		!createInviteMutation.isPending;

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				className="sm:max-w-[500px]"
				onKeyDown={(e) => {
					if (e.key === "Enter" && !e.defaultPrevented) {
						if (inviteUrl) {
							// Success state: close dialog
							handleOpenChange(false);
						}
						else if (canCreate) {
							// Form state: submit if valid
							e.preventDefault();
							handleCreate();
						}
					}
				}}
			>
				<DialogHeader>
					<DialogTitle>Invite User</DialogTitle>
					<DialogDescription>
						Create an invite link to share with a new team member.
					</DialogDescription>
				</DialogHeader>

				{!inviteUrl ? (
					<form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
						<div className="space-y-4 py-4">
							{/* Email */}
							<div className="space-y-2">
								<Label htmlFor="invite-email">Email</Label>
								<Input
									id="invite-email"
									type="email"
									placeholder="user@example.com"
									value={email}
									onChange={(e) => { setEmail(e.target.value); }}
								/>
							</div>

							{/* Account Role (required) */}
							<div className="space-y-2">
								<Label>Account Role (required)</Label>
								<Popover open={accountRoleSelectorOpen} onOpenChange={setAccountRoleSelectorOpen}>
									<PopoverTrigger asChild>
										<Button type="button" variant="outline" className="w-full justify-between">
											{selectedAccountRole?.name ?? "Select role..."}
											<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-[400px] p-0" align="start">
										<Command>
											<CommandInput placeholder="Search roles..." />
											<CommandList>
												<CommandEmpty>No roles found.</CommandEmpty>
												<CommandGroup>
													{rolesQuery.data?.map((role) => (
														<CommandItem
															key={role.id}
															onSelect={() => {
																setSelectedAccountRoleId(role.id);
																setAccountRoleSelectorOpen(false);
															}}
														>
															<Check
																className={cn(
																	"mr-2 h-4 w-4",
																	selectedAccountRoleId === role.id
																		? "opacity-100"
																		: "opacity-0"
																)}
															/>
															<div className="flex flex-col">
																<span>{role.name}</span>
																{role.description && (
																	<span className="text-xs text-muted-foreground">
																		{role.description}
																	</span>
																)}
															</div>
															{role.isSystem && (
																<Badge variant="outline" className="ml-auto text-xs">
																	System
																</Badge>
															)}
														</CommandItem>
													))}
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
								<p className="text-xs text-muted-foreground">
									Account role determines base permissions across all teams
								</p>
							</div>

							{/* Teams (optional) */}
							<div className="space-y-2">
								<Label>Teams (optional)</Label>
								<div className="flex flex-wrap gap-2 min-h-[32px]">
									{selectedTeams.length === 0 ? (
										<span className="text-muted-foreground text-sm">No teams selected</span>
									) : (
										selectedTeams.map((team) => (
											<Badge key={team.id} variant="secondary" className="pr-1">
												{team.name}
												<button
													type="button"
													className="ml-1 rounded-full hover:bg-muted p-0.5"
													onClick={() => { handleToggleTeam(team.id); }}
												>
													<X className="h-3 w-3" />
												</button>
											</Badge>
										))
									)}
								</div>
								<Popover open={teamSelectorOpen} onOpenChange={setTeamSelectorOpen}>
									<PopoverTrigger asChild>
										<Button type="button" variant="outline" className="w-full justify-between">
											Select teams...
											<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-[400px] p-0" align="start">
										<Command>
											<CommandInput placeholder="Search teams..." />
											<CommandList>
												<CommandEmpty>No teams found.</CommandEmpty>
												<CommandGroup>
													{teamsQuery.data?.map((team) => (
														<CommandItem
															key={team.id}
															onSelect={() => { handleToggleTeam(team.id); }}
														>
															<Check
																className={cn(
																	"mr-2 h-4 w-4",
																	selectedTeamIds.includes(team.id)
																		? "opacity-100"
																		: "opacity-0"
																)}
															/>
															{team.name}
														</CommandItem>
													))}
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
							</div>

							{/* Team Role (required if teams selected) */}
							{selectedTeamIds.length > 0 && (
								<div className="space-y-2">
									<Label>Team Role (required)</Label>
									<Popover open={teamRoleSelectorOpen} onOpenChange={setTeamRoleSelectorOpen}>
										<PopoverTrigger asChild>
											<Button type="button" variant="outline" className="w-full justify-between">
												{selectedTeamRole?.name ?? "Select team role..."}
												<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-[400px] p-0" align="start">
											<Command>
												<CommandInput placeholder="Search roles..." />
												<CommandList>
													<CommandEmpty>No roles found.</CommandEmpty>
													<CommandGroup>
														{rolesQuery.data?.map((role) => (
															<CommandItem
																key={role.id}
																onSelect={() => {
																	setSelectedTeamRoleId(role.id);
																	setTeamRoleSelectorOpen(false);
																}}
															>
																<Check
																	className={cn(
																		"mr-2 h-4 w-4",
																		selectedTeamRoleId === role.id
																			? "opacity-100"
																			: "opacity-0"
																	)}
																/>
																<div className="flex flex-col">
																	<span>{role.name}</span>
																	{role.description && (
																		<span className="text-xs text-muted-foreground">
																			{role.description}
																		</span>
																	)}
																</div>
															</CommandItem>
														))}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
									<p className="text-xs text-muted-foreground">
										User will have this role in all selected teams
									</p>
								</div>
							)}

							{/* Immediate Confirmation (Super Admin only) */}
							{isSuperAdmin && (
								<div className="flex items-center space-x-2">
									<Checkbox
										id="confirm-immediately"
										checked={confirmImmediately}
										onCheckedChange={(checked: boolean) => { setConfirmImmediately(checked); }}
									/>
									<Label htmlFor="confirm-immediately" className="text-sm font-normal">
										Confirm user immediately (bypass invite)
									</Label>
								</div>
							)}

							{/* Error */}
							{createInviteMutation.isError && (
								<div className="rounded-md bg-destructive/10 p-3 text-sm text-red-400">
									{createInviteMutation.error.message}
								</div>
							)}
						</div>

						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => { handleOpenChange(false); }}>
								Cancel
							</Button>
							<Button
								type="submit"
								disabled={!canCreate}
							>
								{createInviteMutation.isPending
									? (confirmImmediately ? "Adding..." : "Creating...")
									: (confirmImmediately ? "Add User" : "Create Invite")}
							</Button>
						</DialogFooter>
					</form>
				) : inviteUrl === "__CONFIRMED__" ? (
					/* Success State: User Confirmed Immediately */
					<form onSubmit={(e) => { e.preventDefault(); handleOpenChange(false); }}>
						<div className="space-y-4 py-4">
							<div className="rounded-md bg-emerald-500/10 border border-emerald-400/40 p-3">
								<div className="flex items-center gap-2 text-emerald-300">
									<Check className="h-4 w-4" />
									<span className="text-sm font-medium">
										{email} has been added and can now log in
									</span>
								</div>
							</div>

							<p className="text-xs text-muted-foreground">
								The user has been granted access to the selected teams. They can log in immediately using their email.
							</p>
						</div>

						<DialogFooter>
							<Button type="submit">Done</Button>
						</DialogFooter>
					</form>
				) : (
					/* Success State: Show Invite Link */
					<form onSubmit={(e) => { e.preventDefault(); handleOpenChange(false); }}>
						<div className="space-y-4 py-4">
							<div className="rounded-md bg-emerald-500/10 border border-emerald-400/40 p-3">
								<div className="flex items-center gap-2 text-emerald-300">
									<Check className="h-4 w-4" />
									<span className="text-sm font-medium">
										Invite created! Share this link with {email}
									</span>
								</div>
							</div>

							<div className="flex gap-2">
								<Input
									value={inviteUrl}
									readOnly
									className="font-mono text-sm"
								/>
								<Button type="button" onClick={() => { void handleCopy(); }} variant="outline">
									<Copy className="h-4 w-4" />
								</Button>
							</div>

							<p className="text-xs text-muted-foreground">
								This link expires in 7 days. The user must log in with {email} to accept.
							</p>
						</div>

						<DialogFooter>
							<Button type="submit">Done</Button>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
